//----------------------------------------------------
// Dashboard.jsx - Logboek24 hoofd-dashboard (PL & Admin)
//----------------------------------------------------
import React, { useEffect, useState } from "react";
import { useUser } from "../context/UserContext";
import NieuweWerkbonModal from "./NieuweWerkbonModal";
import { getTemplates } from "../services/api";
import PinturaDemo from "../components/PinturaDemo";
import VideoUpload from "../components/VideoUpload";

export default function Dashboard() {
  const { user, loading, logout } = useUser();

  const [showNieuweWerkbon, setShowNieuweWerkbon] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState("");
  const [layoutMode, setLayoutMode] = useState("touch");
  const roleNames = getRoleNames(user);

  const isPLofAdmin = roleNames.some((role) =>
    ["projectleider", "admin", "platformadmin", "tenantadmin", "zzp_owner"].includes(role)
  );
  const isDesktopLayout = layoutMode === "desktop";

  useEffect(() => {
    function detectLayout() {
      if (typeof window === "undefined") return;

      const width = window.innerWidth || 0;
      const hasHover = window.matchMedia?.("(hover: hover)")?.matches ?? false;
      const finePointer =
        window.matchMedia?.("(pointer: fine)")?.matches ?? false;
      const platform = String(
        navigator?.userAgentData?.platform || navigator?.platform || ""
      ).toLowerCase();
      const isWindows = platform.includes("win");

      const desktopLike = width >= 1024 && (hasHover || finePointer || isWindows);
      setLayoutMode(desktopLike ? "desktop" : "touch");
    }

    detectLayout();
    window.addEventListener("resize", detectLayout);
    return () => window.removeEventListener("resize", detectLayout);
  }, []);

  useEffect(() => {
    if (!user) return;

    async function fetchTemplates() {
      try {
        const data = await getTemplates(user.tenantId, user.rol);
        setTemplates(data);
      } catch (err) {
        setError(err.message);
      }
    }

    fetchTemplates();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white px-6 py-4 rounded-xl shadow">
          Bezig met laden...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white px-6 py-4 rounded-xl shadow">
          Geen geldige sessie.{" "}
          <button
            className="text-blue-600 underline ml-1"
            onClick={() => {
              logout();
              window.location.href = "/login";
            }}
          >
            Opnieuw inloggen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <header className="bg-blue-700 text-white fixed top-0 left-0 right-0 z-50 shadow-md">
        <div
          className={`max-w-7xl mx-auto py-3 flex justify-between items-center ${
            isDesktopLayout ? "px-6" : "px-4"
          }`}
        >
          <h1 className="text-xl font-semibold">Logboek24</h1>

          <div className="flex items-center gap-6">
            <div className="text-sm text-right">
              <p className="font-semibold">{user.naam}</p>
              <p className="text-blue-200 italic">{roleNames.join(", ") || "-"}</p>
            </div>

            <button
              onClick={() => {
                logout();
                window.location.href = "/login";
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
            >
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="h-20"></div>

      <div
        className={`mx-auto ${
          isDesktopLayout
            ? "max-w-7xl p-6 grid grid-cols-12 gap-6"
            : "max-w-6xl p-4 space-y-6"
        }`}
      >
        <section
          className={`bg-white rounded-xl shadow p-6 ${
            isDesktopLayout ? "col-span-12 lg:col-span-4" : ""
          }`}
        >
          <h2 className="text-lg font-bold mb-2">Gebruiker</h2>
          <p>
            <strong>Naam:</strong> {user.naam}
          </p>
          <p>
            <strong>Rol:</strong> {roleNames.join(", ") || "-"}
          </p>
        </section>

        {isPLofAdmin && (
          <section
            className={`bg-white rounded-xl shadow p-6 space-y-4 ${
              isDesktopLayout ? "col-span-12 lg:col-span-8" : ""
            }`}
          >
            <h2 className="text-lg font-bold">Werkbonnen</h2>

            <button
              onClick={() => setShowNieuweWerkbon(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg shadow"
            >
              Nieuwe werkbon
            </button>

            {showNieuweWerkbon && (
              <NieuweWerkbonModal
                onClose={() => setShowNieuweWerkbon(false)}
                onCreated={(wb) => {
                  console.log("Werkbon aangemaakt:", wb);
                  setShowNieuweWerkbon(false);
                }}
              />
            )}
          </section>
        )}

        <section
          className={`bg-white rounded-xl shadow p-6 ${
            isDesktopLayout ? "col-span-12" : ""
          }`}
        >
          <h2 className="text-xl font-bold mb-4">Media / Site Survey</h2>
          <PinturaDemo />
          <VideoUpload />
        </section>

        <section
          className={`bg-white rounded-xl shadow p-6 ${
            isDesktopLayout ? "col-span-12" : ""
          }`}
        >
          <h2 className="text-lg font-bold mb-3">Templates</h2>
          {error && <p className="text-red-600">{error}</p>}

          {templates.length === 0 ? (
            <p className="text-gray-500">Geen templates gevonden.</p>
          ) : (
            <ul className="space-y-3">
              {templates.map((tpl) => (
                <li
                  key={tpl._id}
                  className="bg-gray-50 p-4 rounded-xl border-l-4 border-blue-500"
                >
                  <h3 className="font-semibold">{tpl.title}</h3>
                  <p className="text-sm text-gray-500">{tpl.norm}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).toLowerCase());
  if (user?.role) roleSet.add(String(user.role).toLowerCase());
  for (const role of user?.roles || []) {
    if (typeof role === "string") {
      roleSet.add(role.toLowerCase());
    } else if (role?.naam || role?.name) {
      roleSet.add(String(role.naam || role.name).toLowerCase());
    }
  }
  return Array.from(roleSet);
}
